import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { EMPTY, Observable, switchMap } from 'rxjs';

/**
 * Opt-in preloading: a lazy route is fetched ahead of time only if it declares
 * `data: { preload: true }`, and even then only once the browser goes idle.
 *
 * This replaces `PreloadAllModules`, which fetched every lazy chunk in the app right after the
 * first navigation — so every user downloaded every feature regardless of their roles or where
 * they actually went, which defeats the point of splitting the routes in the first place.
 *
 * Only the hot paths off the orders grid are flagged; reference-data screens load on demand.
 * The idle wait keeps preloading from competing with the orders grid's own initial data fetch
 * and SignalR connect (the router starts preloading as soon as the first navigation ends).
 */
@Injectable({ providedIn: 'root' })
export class OptInIdlePreloadStrategy implements PreloadingStrategy {
  public preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (route.data?.['preload'] !== true) {
      return EMPTY;
    }

    return whenIdle().pipe(switchMap(() => load()));
  }
}

/** Emits once the browser is idle, falling back to a short timer where requestIdleCallback is absent. */
function whenIdle(): Observable<void> {
  return new Observable<void>((subscriber) => {
    const done = () => {
      subscriber.next();
      subscriber.complete();
    };

    if (typeof requestIdleCallback !== 'function') {
      const timer = setTimeout(done, 500);
      return () => clearTimeout(timer);
    }

    // The timeout caps how long a permanently-busy tab can starve the preload.
    const handle = requestIdleCallback(done, { timeout: 3000 });
    return () => cancelIdleCallback(handle);
  });
}
